import React, { useRef, useState, useEffect } from 'react';
import { PromiseState, connect } from 'react-refetch';
import { useSelector, useDispatch } from 'react-redux';
import AnimateHeight from 'react-animate-height';
import debounce from 'lodash.debounce';

import useGroupedEvents from './Unread/useGroupedEvents';
import useAllGroupedEvents from './All/useAllGroupedEvents';
import useMedia from 'hooks/useMedia';
import useOnClickOutside from 'hooks/useOnClickOutside';

import { RootState } from 'redux/rootReducer';
import {
  ISubscription,
  loadSubscriptions,
  selectSubscriptions,
  shouldLoadSubscriptions,
} from 'redux/reducers/subscriptions';

import { loadCountries } from 'redux/reducers/countries';

import getUnreadNotificationsURL from 'url-constructors/getUnreadNotificationsURL';
import getAllNotificationsURL from 'url-constructors/getAllNotificationsURL';
import getAcknowledgeEventURL from 'url-constructors/getAcknowledgeEventURL';
import getAcknowledgeAllEventsURL from 'url-constructors/getAcknowledgeAllEventsURL';

import Layout from 'components/styled/Layout/Layout';
import UnreadEvents from './Unread/UnreadEvents';
import AllEvents from './All/AllEvents';

import { ReactComponent as Bell } from 'icons/notifications-bell.svg';

import {
  BellBox,
  BellButton,
  Absolute,
  Slide,
  Slider,
  SliderContainer,
  AbsoluteMobile,
} from './styles';
import getEntityMetadataByIdsURL from '../../../url-constructors/getEntityMetadataByIdsURL';
import { IMetadata } from './types';
import filterNotFound from '../Settings/utils/filterNotFoundArtists';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import { useAcknowledgeNotFoundArtistEvents } from './utils/useEvents';

import { SHOW_BELL_NOTIFICATION } from 'config';

/**
 * @param {Bool} animate should be false on small screens.
 * This adds an optional AnimateHeight component around the Notifications in order to transform the height of the dropdown when sliding back and forth.
 * On smaller screens we do not want this functionality and thus it should only return its children when `animate` is false. */
const OptionalAnimateHeight: any = ({ animate, children, ...rest }: any) => {
  if (animate) {
    return <AnimateHeight {...rest}>{children}</AnimateHeight>;
  }

  return children;
};

const dataSources = (): any => {
  return {
    unreadNotifications: {
      url: getUnreadNotificationsURL(),
      refreshInterval: 3600000, // 1 hour
    },
    allNotifications: {
      url: getAllNotificationsURL(),
      refreshInterval: 3600000, // 1 hour
    },
    postArtistMetaData: (artistIds: string[]): any => ({
      postArtistMetaDataResponse: {
        method: 'POST',
        url: getEntityMetadataByIdsURL({ entityType: 'artist' }),
        body: JSON.stringify(artistIds),
      },
    }),
    acknowledgeEvent: (id: number): any => ({
      acknowledged: {
        url: getAcknowledgeEventURL(id),
        method: 'POST',
        body: JSON.stringify({
          applicationId: 'opus',
          channelId: 'on_site',
          status: 'acknowledged',
        }),
        andThen: (): any => ({
          unreadNotifications: {
            url: getUnreadNotificationsURL(),
            // `react-refetch` compares request arguments
            // and sends back a cached response when unchanged.
            // We never change arguments so `force` is required.
            force: true,
            refreshing: true,
          },
        }),
      },
    }),
    acknowledgeEntityEvents: (detailPageEntityId: string): any => ({
      acknowledged: {
        url: getAcknowledgeAllEventsURL(),
        method: 'POST',
        body: JSON.stringify({
          entities: [
            {
              id: detailPageEntityId,
              type: 'artist',
            },
          ],
        }),
        andThen: (): any => ({
          unreadNotifications: {
            url: getUnreadNotificationsURL(),
            force: true,
            refreshing: true,
          },
        }),
      },
    }),
    acknowledgeAllEvents: (): any => ({
      acknowledged: {
        url: getAcknowledgeAllEventsURL(),
        method: 'POST',
        andThen: (): any => ({
          unreadNotifications: {
            url: getUnreadNotificationsURL(),
            force: true,
            refreshing: true,
          },
        }),
      },
    }),
  };
};

/*
 * Events: the root component for displaying notifications.
 *
 * - Shows a bell icon with notification count pop-up on top.
 *
 * - Clicking the icon opens the notification modal (absolute positioned).
 *
 * - Shows notifications grouped by artist and sorted by event timestamp.
 *
 * - If an artist has no notifications, only show the name and artwork.
 *
 * - Clicking an event goes to the artist page and subsequently:
 *   - Closes the modal
 *   - Does a POST request to acknowledge the notification.
 *   - Refetches unread notifications so the count and list are updated.
 *
 * - All artists are buttons that switch to a detail screen.
 *   This screen shows all notifications received since the user subscribed.
 *   Notifications here are grouped by day. Data here is compared with the
 *   unread data to see if a notification is new.
 *   In case if selected artist has unread notifications and this detail screen is opened we automatically mark them as read:
 *    - Does a POST request to acknowledge all notification for selected artist.
 *    - Refetches unread notifications so the count and list are updated.
 */
const Events: React.FC<IMessagesProps> = (props): JSX.Element => {
  const {
    unreadNotifications = PromiseState.create(),
    allNotifications = PromiseState.create(),
    acknowledgeEntityEvents,
    acknowledgeEvent,
    acknowledgeAllEvents,
    postArtistMetaData,
    postArtistMetaDataResponse = PromiseState.create(),
    setNotificationsOpened,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [detailPageEntityId, setDetailPageEntityId] = useState<string>();
  const [dropdownHeight, setDropdownHeight] = useState<string | number>('auto');
  const countries = useSelector((state: RootState) => state.countries.data);
  const regions = useSelector((state: RootState) => state.regions.data);
  const dispatch = useDispatch();
  const [subscriptions] = useSelector((state: RootState) =>
    selectSubscriptions(state, 'artist')
  );
  const isLargeScreen = useMedia('(min-width: 50em)');
  const isMobileScreen = useMedia('(max-width: 43.9375em)');
  const modalRef = useRef<HTMLDivElement>(null);
  const artistEvents = useRef<HTMLDivElement>(null);
  const unreadEvents = useRef<HTMLDivElement>(null);
  const slider = useRef<HTMLDivElement>(null);

  const setIsOpenHendler: any = debounce((isOpen: boolean) => {
    if (setNotificationsOpened) {
      setNotificationsOpened(isOpen);
    }
    setIsOpen(isOpen);
  }, 0);

  useOnClickOutside(modalRef, () => isOpen && closeModal());

  const [, pending] = useCombinedRefetchState(
    subscriptions?.length
      ? [unreadNotifications, allNotifications, postArtistMetaDataResponse]
      : [unreadNotifications, allNotifications]
  );

  const totalEvents = unreadNotifications?.value?.totalEvents;
  const totalUnread = totalEvents > 99 ? '99+' : totalEvents;

  const postArtistMetaDataResponseValue =
    postArtistMetaDataResponse.value?.filter(filterNotFound);

  const selectedArtist = postArtistMetaDataResponseValue?.find(findByEntityId);

  function findByEntityId(artist: { entity: IMetadata }): boolean {
    return artist.entity.id === detailPageEntityId;
  }

  // Group events in the root component to prevent visual delay.
  const groupedUnreadEvents = useGroupedEvents(unreadNotifications.value);
  const [groupedAllEvents, totalAllEvents] = useAllGroupedEvents(
    allNotifications.value,
    selectedArtist?.entity?.id
  );

  useAcknowledgeNotFoundArtistEvents(
    groupedUnreadEvents,
    postArtistMetaDataResponse,
    postArtistMetaDataResponseValue,
    acknowledgeEntityEvents
  );

  const closeModal = (): void => {
    // reset dropdown height before close modal to prevent animation on next modal opening
    const unreadElementHeight =
      unreadEvents.current?.getBoundingClientRect().height;
    setDropdownHeight(unreadElementHeight || 'auto');
    setIsOpenHendler(false);
  };

  useEffect(() => {
    if (dispatch(shouldLoadSubscriptions())) {
      dispatch(loadSubscriptions());
    }
    dispatch(loadCountries());
  }, [dispatch]);

  useEffect(() => {
    if (subscriptions?.length) {
      const artistIds = subscriptions.map(mapToIds);

      postArtistMetaData(artistIds);
    }

    function mapToIds(subscription: ISubscription): string {
      return subscription.entityId;
    }
  }, [postArtistMetaData, subscriptions]);

  /*
   * Go back to main screen when closing the modal.
   */
  useEffect(() => {
    if (!isOpen && detailPageEntityId) {
      setDetailPageEntityId(undefined);
    }
  }, [isOpen, detailPageEntityId]);

  /*
   * On mobile, the modal should be fullscreen and scrollable.
   * However, because the body of the page is also scrollable
   * you get two scrollbars and scrolling the body causes a glitch.
   * This effect blocks body scroll on mobile when the modal is open.
   */
  useEffect(() => {
    const $ = document.documentElement;

    if (!isLargeScreen && isOpen) {
      $.classList.add('block-body-scroll');
    }

    return function unmount(): void {
      if ($.classList.contains('block-body-scroll')) {
        $.classList.remove('block-body-scroll');
      }
    };
  }, [isLargeScreen, isOpen]);

  function handleAcknowledgeEvent(eventId: number): void {
    closeModal();
    acknowledgeEvent(eventId);
  }

  function handleAcknowledgeAllEvents(): void {
    closeModal();
    acknowledgeAllEvents();
  }

  function handleArtistEventsPage(entityId: string): void {
    setDetailPageEntityId(entityId);

    /** If we open artist's details page and this artist has some unread events -> acknowledge all events for selected artist */
    if (groupedUnreadEvents && groupedUnreadEvents[entityId])
      acknowledgeEntityEvents(entityId);
  }

  useEffect(() => {
    if (isOpen && isLargeScreen) {
      setTimeout(() => {
        /** Set a pixel height value on the dropdown so that it can animate to there */
        const menuElement = detailPageEntityId
          ? artistEvents.current
          : unreadEvents.current;

        if (menuElement) {
          const { height } = menuElement.getBoundingClientRect();
          setDropdownHeight(height);
        }
      }, 0);
    }
  }, [
    isLargeScreen,
    detailPageEntityId,
    isOpen,
    groupedUnreadEvents,
    groupedAllEvents,
    postArtistMetaDataResponseValue,
  ]);

  return (
    <>
      {SHOW_BELL_NOTIFICATION && (
        <BellBox>
          <BellButton
            isMobileScreen={isMobileScreen}
            isOpen={isOpen}
            onClick={(): void =>
              isOpen ? closeModal() : setIsOpenHendler(!isOpen)
            }
          >
            <Layout.Center>
              <Bell aria-hidden />{' '}
              {totalEvents > 0 && <span>{totalUnread}</span>}
            </Layout.Center>
          </BellButton>

          <Layout.Relative>
            {isOpen && (
              <AbsoluteMobile isMobileScreen={isMobileScreen}>
                <Absolute ref={modalRef} isMobileScreen={isMobileScreen}>
                  <OptionalAnimateHeight
                    animate={isLargeScreen}
                    duration={300}
                    height={dropdownHeight}
                  >
                    <SliderContainer>
                      <Slider detailSection={detailPageEntityId} ref={slider}>
                        <Slide ref={unreadEvents}>
                          <UnreadEvents
                            subscriptions={subscriptions}
                            groupedUnreadEvents={groupedUnreadEvents}
                            countries={countries}
                            regions={regions}
                            artistsMetadata={postArtistMetaDataResponseValue}
                            setDetailPageEntityId={handleArtistEventsPage}
                            handleAcknowledgeEvent={handleAcknowledgeEvent}
                            handleAcknowledgeAllEvents={
                              handleAcknowledgeAllEvents
                            }
                            setIsOpenEvents={setIsOpenHendler}
                            isPending={pending}
                            isMobileScreen={isMobileScreen}
                          />
                        </Slide>
                        <Slide ref={artistEvents}>
                          <AllEvents
                            groupedAllEvents={groupedAllEvents}
                            unreadEvents={unreadNotifications.value}
                            totalEvents={totalAllEvents}
                            countries={countries}
                            regions={regions}
                            entity={selectedArtist?.entity}
                            setDetailPageEntityId={setDetailPageEntityId}
                            setIsOpenEvents={setIsOpenHendler}
                            isMobileScreen={isMobileScreen}
                          />
                        </Slide>
                      </Slider>
                    </SliderContainer>
                  </OptionalAnimateHeight>
                </Absolute>
              </AbsoluteMobile>
            )}
          </Layout.Relative>
        </BellBox>
      )}
    </>
  );
};

interface IMessagesProps {
  unreadNotifications: PromiseState<any>;
  allNotifications: PromiseState<any>;
  postArtistMetaData: (artistIds: string[]) => void;
  postArtistMetaDataResponse: any;
  acknowledgeEvent: (id: number) => any;
  acknowledgeAllEvents: () => any;
  acknowledgeEntityEvents: (detailPageEntityId: string) => any;
  setNotificationsOpened?: any;
}

export default connect(dataSources as any)(Events as any) as any;
